import {
  call,
  put,
  takeEvery,
  select,
  take,
  fork,
  cancel,
  cancelled,
  getContext,
  setContext,
  delay,
} from 'redux-saga/effects';
import { isApiError, requestPuppy, createFormData } from '@wix/da-http-client';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { createUploadChannel } from '@wix/da-shared-react/pkg/redux/uploads/saga/base';
import {
  startImageUpload,
  startMultipleImageUpload,
  addUploadProgressAttachment,
  updateUploadProgress,
  failImageUpload,
  completeImageUpload,
  cancelImageUpload,
} from '../actions/drafts';
import { getDraftMessageAttachments } from '../selectors/drafts';
import { maximumAttachments } from '../../constants';
import ddt from '@wix/da-ddt';
import {
  PapiDaSharedDeviationFetch,
  PapiDaSharedDeviationSubmitUploadDeviation,
  PapiDeviation,
  PapiRequestDaSharedDeviationSubmitUploadDeviation,
} from '@wix/da-papi-types';
import { factoryAttachmentFromDeviationAndType } from '../../utils/attachments';

function isHEIC(file: File): boolean {
  const heicMimeType = 'image/heic';
  const heicExtensions = ['heic', 'heif'];
  const fileType = file.type.toLowerCase();
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  return (
    fileType === heicMimeType ||
    (!!fileExtension && heicExtensions.includes(fileExtension))
  );
}

async function convertHEICtoPNG(file: File): Promise<File | Error> {
  if (typeof window === 'undefined') {
    return new Error('convertHEICtoPNG should only be called in the browser');
  }

  let heic2any;
  try {
    heic2any = (await import('heic2any')).default;
  } catch (e) {
    return new Error('Failed to import heic2any');
  }

  try {
    const conversionResult = await heic2any({
      blob: file,
      toType: 'image/png',
    });

    if (!(conversionResult instanceof Blob)) {
      return new Error('Failed to convert HEIC');
    }

    const pngFile = new File(
      [conversionResult],
      file.name.replace(/\.[^/.]+$/, '.png'),
      { type: 'image/png' }
    );
    return pngFile;
  } catch (e) {
    return new Error('Failed to convert HEIC');
  }
}

function* handleImageUpload(action: ReturnType<typeof startImageUpload>) {
  const { roomId, file, fileId } = action.payload;

  const uploadTasks = yield getContext('uploadTasks');

  try {
    const currentUser = yield select(getCurrentUser);

    if (!currentUser) {
      return;
    }

    // Convert HEIC files to PNG if necessary
    const fileToUpload = isHEIC(file)
      ? yield call(convertHEICtoPNG, file)
      : file;

    if (fileToUpload instanceof Error) {
      ddt.alert('damz', 'Failed to convert HEIC to PNG', ['damz'], {});
      yield put(failImageUpload(roomId, fileId, 'Failed to convert HEIC file'));
      return;
    }

    if (!fileToUpload || !(fileToUpload instanceof File)) {
      ddt.alert('damz', 'Invalid file for upload', ['damz'], {});
      yield put(failImageUpload(roomId, fileId, 'Invalid file'));
      return;
    }

    yield put(addUploadProgressAttachment(roomId, fileId, fileToUpload));

    const formData =
      createFormData<PapiRequestDaSharedDeviationSubmitUploadDeviation>({
        upload_file: fileToUpload,
        folder_name: 'Media Uploads',
      });

    // Create upload channel for progress tracking
    const channel = yield* createUploadChannel(
      '/deviation/submit/upload/deviation',
      formData,
      'deviation'
    );

    let response: PapiDaSharedDeviationSubmitUploadDeviation;

    try {
      while (true) {
        const event = yield take(channel);

        if (event.progress !== undefined) {
          const progress = Math.round(event.progress * 100);
          yield put(updateUploadProgress(roomId, fileId, progress));
        }

        if (event.success) {
          response =
            event.response as PapiDaSharedDeviationSubmitUploadDeviation;
          break;
        }

        if (event.error) {
          throw event.error;
        }
      }
    } finally {
      channel.close();
    }

    // Check if the task was cancelled
    if (yield cancelled()) {
      ddt.log('damz', 'Upload cancelled', { roomId, fileId });
      return;
    }

    if (isApiError(response)) {
      throw new Error(response.error || 'Upload failed');
    }

    yield delay(500);

    const deviation: PapiDaSharedDeviationFetch = yield call(requestPuppy, {
      method: 'get',
      url: '/deviation/fetch',
      params: {
        deviationid: response.privateId,
        type: 'art',
        username: currentUser.username,
      },
    });

    if (!deviation || isApiError(deviation)) {
      throw new Error('Upload success, but failed to fetch deviation');
    }

    const stashAttachment = factoryAttachmentFromDeviationAndType(
      deviation as PapiDeviation
    );

    if (!stashAttachment) {
      throw new Error('Upload success, but failed to create attachment');
    }

    yield put(completeImageUpload(roomId, fileId, stashAttachment));
  } catch (error) {
    if (!(yield cancelled())) {
      ddt.error('damz', 'Failed to upload image', error);
      yield put(
        failImageUpload(roomId, fileId, error.message || 'Upload failed')
      );
    }
  } finally {
    const taskKey = `${roomId}-${fileId}`;
    uploadTasks.delete(taskKey);
  }
}

function* handleMultipleImageUpload(
  action: ReturnType<typeof startMultipleImageUpload>
) {
  const { roomId, files } = action.payload;

  // Get current attachments to check against limit
  const currentAttachments = yield select(getDraftMessageAttachments, roomId);
  const currentAttachmentCount = currentAttachments.length;

  // Calculate how many files we can actually upload
  const remainingSlots = maximumAttachments - currentAttachmentCount;

  if (remainingSlots <= 0) {
    ddt.log(
      'image-upload',
      'Attachment limit reached, skipping multiple upload',
      {
        currentAttachmentCount,
        maximumAttachments,
        requestedFiles: files.length,
      }
    );
    return;
  }

  // Limit files to upload based on remaining slots
  const filesToUpload = files.slice(0, remainingSlots);

  if (filesToUpload.length < files.length) {
    ddt.log('image-upload', 'Some files skipped due to attachment limit', {
      totalFiles: files.length,
      filesToUpload: filesToUpload.length,
      remainingSlots,
    });
  }

  for (const file of filesToUpload) {
    yield put(
      startImageUpload(
        roomId,
        file,
        `${file.name}-${Date.now()}-${Math.random()}`
      )
    );
  }
}

function* handleCancelImageUpload(
  action: ReturnType<typeof cancelImageUpload>
) {
  const { roomId, fileId } = action.payload;

  const uploadTasks = yield getContext('uploadTasks');

  const taskKey = `${roomId}-${fileId}`;
  if (uploadTasks && uploadTasks.has(taskKey)) {
    const task = uploadTasks.get(taskKey);

    if (!task.isCancelled()) {
      yield cancel(task);
      ddt.log('damz', 'Cancelled upload task', { roomId, fileId });
    }

    uploadTasks.delete(taskKey);
  }
}

function* watchImageUploads() {
  let uploadTasks = yield getContext('uploadTasks');
  if (!uploadTasks) {
    uploadTasks = new Map();
    yield setContext({ uploadTasks });
  }

  while (true) {
    const action = yield take(startImageUpload);
    const { roomId, fileId } = action.payload;

    const task = yield fork(handleImageUpload, action);
    const taskKey = `${roomId}-${fileId}`;
    uploadTasks.set(taskKey, task);
  }
}

export function* imageUploadSaga() {
  yield setContext({ uploadTasks: new Map() });
  yield takeEvery(startMultipleImageUpload, handleMultipleImageUpload);
  yield takeEvery(cancelImageUpload, handleCancelImageUpload);
  yield fork(watchImageUploads);
}
